// What a player's history says about them, worked out from the plays their device holds rather than asked
// of the server: whether they've played a game enough to skip its tutorial, and which games they've
// finished a real puzzle of.

/** A board the player has completed, as far as these questions need to know it. */
export interface CompletedBoard {
  /** Null on a play the server hasn't yet said which game it belongs to; it counts for none. */
  gameId: number | null;
  boardId: number;
  /** The slug of a tutorial or practice board; null for a day's puzzle. */
  boardSlug?: string | null;
}

/** How many different boards of a game it takes to count as knowing it. */
export const AUTO_GRADUATE_COMPLETED_COUNT = 2;

/** Tutorial boards are the ones slugged `tutorial…`: a game's own, and the stages of a multi-part one. */
export const isTutorialSlug = (slug: string | null | undefined): boolean => !!slug && slug.startsWith('tutorial');

/**
 * Whether a player has played a game enough to be taken out of its intro mode: they've finished its
 * tutorial, or enough different boards of it that they evidently don't need one. Replays of one board
 * count once.
 */
export function hasGraduatedFromIntro(completed: CompletedBoard[], gameId: number, tutorialSlug: string): boolean {
  const ofGame = completed.filter((board) => board.gameId === gameId);
  if (ofGame.some((board) => board.boardSlug === tutorialSlug)) return true;
  return new Set(ofGame.map((board) => board.boardId)).size >= AUTO_GRADUATE_COMPLETED_COUNT;
}

/** The games a player has finished at least one real puzzle of: anything but a tutorial. */
export function gamesWithRealCompletion(completed: CompletedBoard[]): Set<number> {
  const games = new Set<number>();
  for (const board of completed) {
    if (board.gameId !== null && !isTutorialSlug(board.boardSlug)) games.add(board.gameId);
  }
  return games;
}
